import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import {
  getConsolidatedRecipeByMultipleProduct,
  getInnerConsolidatedRecipe,
  getRecipeByProduct
} from '../../ApiServices/RecipeBuilderService';
import { t } from 'i18next';

export const initialState = {
  value: { totalCount: -1, data: [{}] },
  loading: false,
  error: null,
  selectedValue: null,
  outerValue: { details: [], orderIds: [], orderCount: -1 },
  historyValue: { products: [], _id: null },
  rowsPerPage: 10,
  pageNo: 0,
  sortBy: '',
  sortDirection: 0,
  filter: {}
};

const recipesSlice = createSlice({
  name: 'recipes',
  initialState: initialState,
  reducers: {
    clearRecipesState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.outerValue = initialState.outerValue;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchRecipeByProduct.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchRecipeByProduct.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.selectedValue = initialState?.selectedValue;
      state.filter = action?.meta?.arg;
    });

    builder.addCase(fetchConsolidatedRecipe.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.outerValue = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchConsolidatedRecipe.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.outerValue = initialState.outerValue;
      state.selectedValue = initialState?.selectedValue;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchInnerConsolidatedRecipe.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.historyValue = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchInnerConsolidatedRecipe.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.historyValue = initialState.historyValue;

      state.filter = action?.meta?.arg;
    });
  }
});

export const recipesState = (state: RootState) => state?.recipes;
export const { clearRecipesState } = recipesSlice.actions;
export default recipesSlice.reducer;

export const fetchRecipeByProduct = createAsyncThunk(
  'recipes/fetchRecipeByProduct',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_RECIPE') }));
      let response: any = await getRecipeByProduct(payload);
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchConsolidatedRecipe = createAsyncThunk(
  'recipes/fetchConsolidatedRecipe',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_RECIPE_DETAILS') }));
      let response: any = await getConsolidatedRecipeByMultipleProduct(payload);
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchInnerConsolidatedRecipe = createAsyncThunk(
  'recipes/fetchInnerConsolidatedRecipe',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_RECIPE_DETAILS') }));
      let response: any = await getInnerConsolidatedRecipe(payload);
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadRecipeBuilder = createAsyncThunk(
  'recipe/downloadRecipeBuilder',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_RECIPE_BUILDER_SINGLE') }));
      let response: any = await getRecipeByProduct(payload);
      if (response === 200) dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadConsolidateRecipeBuilder = createAsyncThunk(
  'recipe/downloadConsolidateRecipeBuilder',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_CONSOLIDATED_RECIPE_BUILDER') }));
      let response: any = await getConsolidatedRecipeByMultipleProduct(payload);
      if (response === 200) dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);
